class LoadMoreProducts extends HTMLElement {
  constructor() {
    super();

    this.handleFacetUpdate = this.handleFacetUpdate.bind(this);
    this.loadMoreProducts = this.loadMoreProducts.bind(this);
    this.handleDocumentClick = this.handleDocumentClick.bind(this);

    this.isLoading = false;
    this.isInitialized = false;
    this.currentPage = 1;
    this.totalPages = 1;
    this.totalProducts = 0;
    this.loadedProducts = 0;
    this.pageSize = 16;

    this.productGrid = null;
    this.paginationInfo = null;
    this.loadMoreBtn = null;
    this.productsCountDisplay = null;

    this.progressContainer = null;
    this.progressBar = null;

    this.paginationContainer = null;
  }

  connectedCallback() {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.init());
    } else {
      this.init();
    }
  }

  disconnectedCallback() {
    this.cleanup();
  }

  init() {
    if (this.isInitialized) return;

    try {
      this.setupElements();
      this.setupEventListeners();
      this.updateState();
      this.isInitialized = true;
    } catch (error) {
      console.error('InfiniteScroll: Initialization failed', error);
    }
  }

  setupElements() {
    const productGridSelector = this.dataset.productGrid || '#product-grid';
    const paginationInfoSelector = this.dataset.paginationInfo || '#pagination-info';
    const loadMoreBtnSelector = this.dataset.loadMoreBtn || '.load-more-btn';
    const productsCountDisplaySelector = this.dataset.productsCountDisplay || '#products-count-display';

    this.productGrid = document.querySelector(productGridSelector);
    this.paginationInfo = document.querySelector(paginationInfoSelector);
    this.loadMoreBtn = document.querySelector(loadMoreBtnSelector);
    this.productsCountDisplay = document.querySelector(productsCountDisplaySelector);

    this.progressContainer = document.querySelector('.inner__progress-container') || null;
    this.progressBar = document.querySelector('.inner__progress-bar') || null;

    this.paginationContainer = document.querySelector('.pagination__btn-container') || null;

    if (!this.productGrid) {
      throw new Error(`Product grid not found: ${productGridSelector}`);
    }

    const pageSizeAttr = parseInt(this.productGrid.dataset.pageSize);
    if (!Number.isNaN(pageSizeAttr) && pageSizeAttr > 0) {
      this.pageSize = pageSizeAttr;
    } else if (this.productGrid.dataset.paginatePageSize) {
      const alt = parseInt(this.productGrid.dataset.paginatePageSize);
      if (!Number.isNaN(alt) && alt > 0) this.pageSize = alt;
    }
  }

  setupEventListeners() {
    this.cleanup();

    this.loadMoreBtn = document.querySelector('.load-more-btn');

    if (this.loadMoreBtn) {
      this.loadMoreBtn.addEventListener('click', this.loadMoreProducts);
      console.log('LoadMoreProducts: Button event listener attached');
    } else {
      console.log('LoadMoreProducts: Button not found');
    }

    document.addEventListener('click', this.handleDocumentClick);
    document.addEventListener('facet:update', this.handleFacetUpdate);

    console.log('LoadMoreProducts: Event listeners attached');
  }

  handleDocumentClick(event) {
    if (event.target && event.target.classList.contains('load-more-btn')) {
      event.preventDefault();
      if (!this.isLoading) {
        this.loadMoreProducts();
      }
    }
  }

  cleanup() {
    if (this.loadMoreBtn) {
      this.loadMoreBtn.removeEventListener('click', this.loadMoreProducts);
    }

    document.removeEventListener('click', this.handleDocumentClick);
    document.removeEventListener('facet:update', this.handleFacetUpdate);
  }

  updateState() {
    if (!this.productGrid) return;

    this.currentPage = parseInt(this.productGrid.dataset.currentPage) || this.currentPage || 1;
    this.totalPages = parseInt(this.productGrid.dataset.totalPages) || this.totalPages || 1;
    this.totalProducts = parseInt(this.productGrid.dataset.productsCount) || this.totalProducts || 0;
    const productNodes = this.productGrid.querySelectorAll('.custom-grid-item');
    this.loadedProducts = productNodes.length || this.productGrid.children.length;

    this.updatePaginationInfo();
    this.updateProductsCount();
    this.updateProgressBar();
    this.updateLoadMoreButton();
  }

  updatePaginationInfo() {
    if (!this.paginationInfo) return;

    const showing = Math.min(this.loadedProducts, this.totalProducts || this.loadedProducts);
    this.paginationInfo.innerHTML = `Showing ${showing} of ${this.totalProducts} products`;
  }

  computeRemaining() {
    const remaining = Math.max((this.totalProducts || 0) - this.loadedProducts, 0);
    const remainingToLoad = Math.min(remaining, this.pageSize || 16);
    return { remaining, remainingToLoad };
  }

  updateLoadMoreButton() {
    if (this.paginationContainer) {
      if (this.loadedProducts >= this.totalProducts && this.totalProducts > 0) {
        this.paginationContainer.style.display = 'none';
        return;
      } else {
        this.paginationContainer.style.display = 'block';
      }
    }

    if (!this.loadMoreBtn) return;

    const { remaining, remainingToLoad } = this.computeRemaining();

    if (remaining <= 0 || this.currentPage >= this.totalPages) {
      this.loadMoreBtn.style.display = 'none';
    } else {
      this.loadMoreBtn.style.display = 'inline-block';
      this.loadMoreBtn.disabled = false;
      this.loadMoreBtn.textContent = `Load ${remainingToLoad} More Products`;
      this.loadMoreBtn.setAttribute('aria-live', 'polite');
    }
  }

  updateProductsCount() {
    if (!this.productsCountDisplay) return;

    if (this.loadedProducts >= this.totalProducts && this.totalProducts > 0) {
      this.productsCountDisplay.textContent = 'All products seen';
      this.productsCountDisplay.classList.remove('hidden');
    } else {
      this.productsCountDisplay.textContent = `${this.loadedProducts} products seen`;
      this.productsCountDisplay.classList.remove('hidden');
    }
  }

  updateProgressBar() {
    if (!this.progressContainer && !this.progressBar) return;

    let pct = 0;
    if (this.totalProducts && this.totalProducts > 0) {
      pct = (this.loadedProducts / this.totalProducts) * 100;
    }
    if (pct > 100) pct = 100;
    if (pct < 0) pct = 0;

    const pctStr = Math.round(pct * 100) / 100;

    if (this.progressContainer) {
      this.progressContainer.style.setProperty('--progress-bar', `${pctStr}%`);
      this.progressContainer.setAttribute('aria-valuenow', String(pctStr));
    }

    if (this.progressBar) {
      this.progressBar.style.width = `${pctStr}%`;
      const parent = this.progressBar.closest('[role="progressbar"]');
      if (parent) parent.setAttribute('aria-valuenow', String(pctStr));
    }
  }

  handleFacetUpdate() {
    this.isLoading = false;

    setTimeout(() => {
      this.setupElements();
      this.setupEventListeners();
      this.updateState();
    }, 100);
  }

  getCurrentUrlParams() {
    const urlParams = new URLSearchParams(window.location.search);
    const params = {};

    for (const [key, value] of urlParams.entries()) {
      if (key !== 'page') {
        params[key] = value;
      }
    }

    return params;
  }

  buildNextUrl(page) {
    const params = this.getCurrentUrlParams();
    params.page = page;
    const queryString = new URLSearchParams(params).toString();
    return `${window.location.pathname}?${queryString}`;
  }

  async loadMoreProducts() {
    if (this.isLoading || this.currentPage >= this.totalPages) {
      return;
    }

    this.isLoading = true;

    try {
      const nextPage = this.currentPage + 1;
      const nextUrl = this.buildNextUrl(nextPage);

      const response = await fetch(nextUrl, {
        headers: {
          Accept: 'text/html',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const html = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');

      const serverGrid = doc.querySelector('#product-grid');
      if (serverGrid) {
        const dsCurrent = parseInt(serverGrid.dataset.currentPage);
        const dsTotalPages = parseInt(serverGrid.dataset.totalPages);
        const dsProductsCount = parseInt(serverGrid.dataset.productsCount);
        const dsPageSize = parseInt(serverGrid.dataset.pageSize || serverGrid.dataset.paginatePageSize);

        if (!Number.isNaN(dsTotalPages)) this.totalPages = dsTotalPages;
        if (!Number.isNaN(dsProductsCount)) this.totalProducts = dsProductsCount;
        if (!Number.isNaN(dsPageSize)) this.pageSize = dsPageSize;

        if (this.productGrid) {
          if (!Number.isNaN(dsCurrent)) this.productGrid.dataset.currentPage = String(dsCurrent);
          if (!Number.isNaN(dsTotalPages)) this.productGrid.dataset.totalPages = String(dsTotalPages);
          if (!Number.isNaN(dsProductsCount)) this.productGrid.dataset.productsCount = String(dsProductsCount);
          if (!Number.isNaN(dsPageSize)) this.productGrid.dataset.pageSize = String(dsPageSize);
        }
      } else {
        if (this.productGrid) {
          this.productGrid.dataset.currentPage = String(nextPage);
        }
      }

      const newProducts = doc.querySelectorAll('#product-grid .custom-grid-item');

      if (newProducts.length > 0) {
        this.appendProducts(newProducts);
        this.currentPage = nextPage;
        if (this.productGrid) this.productGrid.dataset.currentPage = String(this.currentPage);
        const productNodes = this.productGrid.querySelectorAll('.custom-grid-item');
        this.loadedProducts = productNodes.length || this.loadedProducts + newProducts.length;
        this.updateState();
        this.reinitializeScripts();
        //Re-initialize product card slider to fix the issue of swiper not initializing on the first load of the section
        window.initProductCardSlider();
      } else {
        this.updateState();
      }
    } catch (error) {
      console.error('InfiniteScroll: Error loading more products', error);
    } finally {
      this.isLoading = false;
    }
  }

  appendProducts(newProducts) {
    const fragment = document.createDocumentFragment();

    newProducts.forEach((product, index) => {
      const cascadeElement = product.querySelector('[data-cascade]');
      if (cascadeElement) {
        const newOrder = this.loadedProducts + index + 1;
        cascadeElement.style.setProperty('--animation-order', newOrder);
      }

      fragment.appendChild(product);
    });

    this.productGrid.appendChild(fragment);
  }

  reinitializeScripts() {
    if (window.theme && typeof window.theme.initializeProductCards === 'function') {
      window.theme.initializeProductCards();
    }

    this.dispatchEvent(
      new CustomEvent('load-more:products-loaded', {
        detail: {
          currentPage: this.currentPage,
          loadedProducts: this.loadedProducts,
          totalProducts: this.totalProducts,
        },
      })
    );
  }

  reset() {
    this.isLoading = false;
    this.updateState();
  }

  getState() {
    return {
      isLoading: this.isLoading,
      currentPage: this.currentPage,
      totalPages: this.totalPages,
      totalProducts: this.totalProducts,
      loadedProducts: this.loadedProducts,
      pageSize: this.pageSize,
    };
  }
}

customElements.define('load-more-products', LoadMoreProducts);
window.LoadMoreProducts = LoadMoreProducts;


if (!customElements.get('grid-layout-switcher')) {
  // Persists the user's View choice (independent of the One/Four <-> Two label swap at the mobile
  // breakpoint, which is purely cosmetic) so it carries over to the next collection page - e.g.
  // via the Sub-Collection/Redirect Bar - instead of resetting to the width-based default.
  var GRID_LAYOUT_STORAGE_KEY = 'tamannaahGridLayoutPreference';

  function getSavedGridLayoutPreference() {
    try {
      return window.localStorage.getItem(GRID_LAYOUT_STORAGE_KEY);
    } catch (error) {
      return null;
    }
  }

  function saveGridLayoutPreference(value) {
    try {
      window.localStorage.setItem(GRID_LAYOUT_STORAGE_KEY, value);
    } catch (error) {
      /* storage unavailable - selection just won't persist across navigation */
    }
  }

  customElements.define(
    'grid-layout-switcher',
    class GridLayoutSwitcher extends HTMLElement {
      constructor() {
        super();
        this.grid = null;
        this.form = null;
        this.twoColRadio = null;
        this.fourColRadio = null;
      }

      connectedCallback() {
        this.init();
      }

      init() {
        if (this.dataset.glsInitialized === 'true') return;
        this.dataset.glsInitialized = 'true';

        this.form = this.querySelector('form');
        this.grid = document.querySelector('#product-grid');
        this.twoColRadio = document.querySelector('#grid-layout--two');
        this.fourColRadio = document.querySelector('#grid-layout--four');

        if (!this.form || !this.grid) return;

        this.setDefaultLayout();

        this.form.addEventListener('change', this.handleChange.bind(this));

        let resizeTimeout;
        window.addEventListener('resize', () => {
          clearTimeout(resizeTimeout);
          resizeTimeout = setTimeout(() => {
            this.handleResize();
          }, 250);
        });
      }

      setDefaultLayout() {
        const savedPreference = getSavedGridLayoutPreference();

        if (savedPreference === 'two') {
          this.applyTwoColumnLayout();
          return;
        }

        if (savedPreference === 'four') {
          this.applyFourColumnLayout();
          return;
        }

        const screenWidth = window.innerWidth;

        if (screenWidth > 1024) {
          this.applyFourColumnLayout();
        } else {
          this.applyTwoColumnLayout();
        }
      }

      handleChange(e) {
        if (!this.grid) return;

        if (e.target.value.includes('two')) {
          this.applyTwoColumnLayout();
          saveGridLayoutPreference('two');
        } else {
          this.applyFourColumnLayout();
          saveGridLayoutPreference('four');
        }
      }

      handleResize() {
        console.log('Window resized to:', window.innerWidth);
      }

      applyTwoColumnLayout() {
        // 'default-desktop' + custom-grid--2-col-desktop together match a higher-specificity CSS
        // rule (template-collection.css) that renders 4 columns as a no-JS fallback. It's only
        // meant for the very first paint, before any layout has been explicitly chosen, so it
        // must be cleared here too - otherwise restoring a saved "two" preference straight from
        // page load (skipping applyFourColumnLayout, which was the only place this was removed)
        // leaves it in place and desktop silently renders 4 columns despite Two being selected.
        this.grid.classList.remove('custom-grid--4-col-desktop', 'custom-grid--1-col-tablet-down', 'default-desktop');
        this.grid.classList.add('custom-grid--2-col-desktop', 'custom-grid--2-col-tablet-down');

        if (this.twoColRadio) {
          this.twoColRadio.checked = true;
        }
        if (this.fourColRadio) {
          this.fourColRadio.checked = false;
        }
      }

      applyFourColumnLayout() {
        this.grid.classList.remove('custom-grid--2-col-desktop', 'custom-grid--2-col-tablet-down', 'default-desktop');
        this.grid.classList.add('custom-grid--4-col-desktop', 'custom-grid--1-col-tablet-down');

        if (this.fourColRadio) {
          this.fourColRadio.checked = true;
        }
        if (this.twoColRadio) {
          this.twoColRadio.checked = false;
        }
      }
    }
  );
}

if (!customElements.get('filter-drawer')) {
  customElements.define(
    'filter-drawer',
    class FilterDrawer extends HTMLElement {
      constructor() {
        super();
      }

      connectedCallback() {
        if (this.dataset.fdInitialized === 'true') return;
        this.dataset.fdInitialized = 'true';

        const body = document.body;

        const openButton = this.querySelector('.mobile-facets__open');
        if (openButton) {
          openButton.addEventListener('click', (e) => {
            this.openFilter();
          });
        }

        document.querySelectorAll('.facet-filters-overlay, .mobile-facets__close').forEach((ele) => {
          ele.addEventListener('click', (e) => {
            const filterDrawer = e.target.closest('filter-drawer');
            if (filterDrawer) {
              filterDrawer.closeFilter();
            }
          });
        });
      }

      openFilter() {
        const openEvent = new CustomEvent('filterOpen', {
          bubbles: true,
          cancelable: true,
          detail: {
            timestamp: Date.now(),
            element: this,
          },
        });

        if (this.dispatchEvent(openEvent)) {
          this.classList.add('active');
          document.body.style.overflow = 'hidden';

          const openedEvent = new CustomEvent('filterOpened', {
            bubbles: true,
            detail: {
              timestamp: Date.now(),
              element: this,
            },
          });
          this.dispatchEvent(openedEvent);
        }
      }

      closeFilter() {
        const closeEvent = new CustomEvent('filterClose', {
          bubbles: true,
          cancelable: true,
          detail: {
            timestamp: Date.now(),
            element: this,
          },
        });

        if (this.dispatchEvent(closeEvent)) {
          this.classList.remove('active');
          document.body.style.overflow = 'auto';

          const closedEvent = new CustomEvent('filterClosed', {
            bubbles: true,
            detail: {
              timestamp: Date.now(),
              element: this,
            },
          });
          this.dispatchEvent(closedEvent);
        }
      }

      open() {
        this.openFilter();
      }

      close() {
        this.closeFilter();
      }

      toggle() {
        if (this.classList.contains('active')) {
          this.closeFilter();
        } else {
          this.openFilter();
        }
      }

      isOpen() {
        return this.classList.contains('active');
      }
    }
  );
}

(function () {
  // Moves the View toggle / Filter & Sort trigger into the Sub-Collection Bar's slots on desktop
  // (or back to their default anchors below the bar on mobile/tablet), and keeps the active
  // sub-collection tab scrolled into view on mobile/tablet. No-ops harmlessly wherever the bar,
  // its slots, or these controls don't exist - safe to run unconditionally on every collection page.
  function relocateCollectionControls() {
    var isDesktop = window.matchMedia('(min-width: 1025px)').matches;
    var viewToggle = document.getElementById('CollectionViewToggle');
    var filterSort = document.getElementById('CollectionFilterSortDrawer');
    var viewSlot = document.querySelector('[data-view-slot]');
    var filterSlot = document.querySelector('[data-filter-slot]');
    var viewAnchor = document.getElementById('CollectionViewToggleAnchor');
    var filterAnchor = document.getElementById('CollectionFilterSortAnchor');

    if (isDesktop && viewSlot && viewToggle && viewToggle.parentElement !== viewSlot) {
      viewSlot.appendChild(viewToggle);
    } else if (!isDesktop && viewAnchor && viewToggle && viewToggle.previousElementSibling !== viewAnchor) {
      viewAnchor.parentNode.insertBefore(viewToggle, viewAnchor.nextSibling);
    }

    if (isDesktop && filterSlot && filterSort && filterSort.parentElement !== filterSlot) {
      filterSlot.appendChild(filterSort);
    } else if (!isDesktop && filterAnchor && filterSort && filterSort.previousElementSibling !== filterAnchor) {
      filterAnchor.parentNode.insertBefore(filterSort, filterAnchor.nextSibling);
    }
  }

  // Scrolls only the tabs strip itself (never the page) by setting scrollLeft directly - .sub-
  // collection-bar__tabs has scroll-behavior: smooth in CSS, so this still animates.
  function scrollActiveSubCollectionTabIntoView() {
    if (window.matchMedia('(min-width: 1025px)').matches) return;
    var tabsContainer = document.querySelector('[data-sub-collection-tabs]');
    if (!tabsContainer) return;
    var activeTab = tabsContainer.querySelector('.sub-collection-bar__tab.is-active');
    if (!activeTab) return;
    tabsContainer.scrollLeft = activeTab.offsetLeft - (tabsContainer.clientWidth - activeTab.clientWidth) / 2;
  }

  function refresh() {
    relocateCollectionControls();
    scrollActiveSubCollectionTabIntoView();
  }

  refresh();

  var resizeTimeout;
  window.addEventListener('resize', function () {
    clearTimeout(resizeTimeout);
    resizeTimeout = setTimeout(refresh, 200);
  });

  // The theme editor replaces a section's markup without firing DOMContentLoaded again, so
  // without this, toggling settings leaves the controls stuck below the bar until a hard refresh.
  if (window.Shopify && Shopify.designMode) {
    document.addEventListener('shopify:section:load', refresh);
  }
})();

(function () {
  // One-time, mobile-only hint: nudges the Redirect Bar's tabs right then back once, to signal
  // the row is horizontally scrollable. Shown at most once ever per browser (localStorage), and
  // only when there's actually more to scroll to - a no-op wherever the bar doesn't exist.
  var HINT_STORAGE_KEY = 'tamannaahSubCollectionBarScrollHintShown';

  function hasHintBeenShown() {
    try {
      return !!window.localStorage.getItem(HINT_STORAGE_KEY);
    } catch (error) {
      return true; // storage unavailable - skip rather than risk showing it on every visit
    }
  }

  function markHintAsShown() {
    try {
      window.localStorage.setItem(HINT_STORAGE_KEY, 'true');
    } catch (error) {
      /* no-op */
    }
  }

  function playScrollHint() {
    if (window.matchMedia('(min-width: 1025px)').matches) return;
    if (hasHintBeenShown()) return;

    var tabsContainer = document.querySelector('[data-sub-collection-tabs]');
    if (!tabsContainer) return;

    var cancelled = false;
    function cancel() {
      cancelled = true;
    }
    // Any real user interaction with the strip cancels the hint immediately, so it never fights
    // the user's own scrolling.
    tabsContainer.addEventListener('touchstart', cancel, { once: true, passive: true });
    tabsContainer.addEventListener('wheel', cancel, { once: true, passive: true });
    tabsContainer.addEventListener('pointerdown', cancel, { once: true, passive: true });

    setTimeout(function () {
      if (cancelled) return;

      var maxScroll = tabsContainer.scrollWidth - tabsContainer.clientWidth;
      if (maxScroll <= 0) return; // nothing to scroll to - hint would be pointless

      markHintAsShown();

      var startScrollLeft = tabsContainer.scrollLeft;
      var nudge = Math.min(24, maxScroll);

      // .sub-collection-bar__tabs already has scroll-behavior: smooth, so these plain scrollLeft
      // assignments animate on their own - no transform, no new DOM, no extra CSS needed.
      tabsContainer.scrollLeft = startScrollLeft + nudge;

      setTimeout(function () {
        if (cancelled) return;
        tabsContainer.scrollLeft = startScrollLeft;
      }, 320);
    }, 500);
  }

  playScrollHint();
})();
